import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useAsset,
  useAssetAssignments,
  useAssetMaintenance,
  useUpdateAsset,
  useDeleteAsset,
  useCheckoutAsset,
  useCheckinAsset,
  useTransferAsset,
  useCreateMaintenanceRecord,
} from '../api/queries/assets';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import { DateInput } from '../components/DateInput';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  Table,
  Tabs,
  Textarea,
  type BadgeTone,
  type TabItem,
} from '../components/ui';
import type {
  Asset,
  AssetAssignment,
  AssetAssignmentEventType,
  AssetStatus,
  AssetType,
  MaintenanceRecord,
  MaintenanceType,
} from '@dsr/shared-types';
import './AssetsPage.css';

// ---------------------------------------------------------------------------
// Display maps & formatters
// ---------------------------------------------------------------------------

const TYPE_LABEL: Record<AssetType, string> = {
  radio: 'Radio',
  vehicle: 'Vehicle',
  firearm: 'Firearm',
  body_armor: 'Body armor',
  bodycam: 'Body camera',
  surveillance_kit: 'Surveillance kit',
  medical_kit: 'Medical kit',
  key: 'Key',
  access_card: 'Access card',
  phone: 'Phone',
  laptop: 'Laptop',
  drone: 'Drone',
  other: 'Other',
};

const STATUS_LABEL: Record<AssetStatus, string> = {
  available: 'Available',
  checked_out: 'Checked out',
  maintenance: 'Maintenance',
  retired: 'Retired',
  lost: 'Lost',
};

const STATUSES: AssetStatus[] = [
  'available', 'checked_out', 'maintenance', 'retired', 'lost',
];

const STATUS_TONE: Record<AssetStatus, BadgeTone> = {
  available: 'success',
  checked_out: 'accent',
  maintenance: 'warn',
  retired: 'neutral',
  lost: 'danger',
};

const EVENT_LABEL: Record<AssetAssignmentEventType, string> = {
  checkout: 'Checked out',
  checkin: 'Checked in',
  transfer: 'Transferred',
  maintenance_out: 'Sent to maintenance',
  maintenance_in: 'Returned from maintenance',
  decommission: 'Decommissioned',
};

const MAINTENANCE_TYPES: MaintenanceType[] = [
  'inspection', 'repair', 'calibration', 'service',
];
const MAINTENANCE_LABEL: Record<MaintenanceType, string> = {
  inspection: 'Inspection',
  repair: 'Repair',
  calibration: 'Calibration',
  service: 'Service',
};

function fmtDateTime(epoch: number | null | undefined): string {
  if (epoch === null || epoch === undefined || !Number.isFinite(epoch)) return '—';
  return new Date(epoch).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function fmtDate(epoch: number | null | undefined): string {
  if (epoch === null || epoch === undefined || !Number.isFinite(epoch)) return '—';
  return new Date(epoch).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function fmtMoney(amount: string | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function fieldError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 403) {
    return 'You do not have permission to do that.';
  }
  if (err instanceof ApiError && err.status === 400) {
    const body = err.body as
      | { details?: { fieldErrors?: Record<string, string[]> } }
      | null;
    const fe = body?.details?.fieldErrors;
    if (fe) {
      const first = Object.values(fe).find((a) => Array.isArray(a) && a.length > 0);
      if (first && Array.isArray(first)) return first[0]!;
    }
  }
  return fallback;
}

// ===========================================================================
// Page
// ===========================================================================

export function AssetDetailPage(): JSX.Element {
  const { id: rawId } = useParams<{ id: string }>();
  const id = Number(rawId);
  const navigate = useNavigate();

  const assetQuery = useAsset(Number.isInteger(id) && id > 0 ? id : null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [custodyAction, setCustodyAction] = useState<'checkout' | 'checkin' | 'transfer' | null>(null);
  const [showMaintenance, setShowMaintenance] = useState(false);

  const deleteAsset = useDeleteAsset();

  if (assetQuery.isPending) {
    return <p className="as-status">Loading asset…</p>;
  }
  if (assetQuery.error) {
    const status = assetQuery.error instanceof ApiError ? assetQuery.error.status : 0;
    // The API returns 404 (not 403) for firearm rows when the viewer lacks
    // asset.firearm_read, so existence is never leaked.
    const msg =
      status === 404 ? 'Asset not found (or restricted).'
        : status === 403 ? 'You do not have permission to view this asset.'
          : 'Failed to load the asset.';
    return (
      <section className="page">
        <p className="as-status as-error">{msg}</p>
        <Link className="as-back" to="/assets">← Back to inventory</Link>
      </section>
    );
  }

  const asset = assetQuery.data.asset;
  const isFirearm = asset.asset_type === 'firearm';
  const canSeeFirearmTab = isFirearm && can('asset.firearm_read');

  const onDelete = async () => {
    try {
      await deleteAsset.mutateAsync(asset.id);
      toast.success('Asset removed.');
      navigate('/assets');
    } catch (err) {
      toast.danger(fieldError(err, 'Could not delete the asset.'));
      setShowDelete(false);
    }
  };

  const tabItems: TabItem[] = [
    { value: 'custody', label: 'Custody timeline', content: <CustodyTab assetId={asset.id} /> },
    ...(can('maintenance.read')
      ? [{
          value: 'maintenance',
          label: 'Maintenance',
          content: <MaintenanceTab assetId={asset.id} onLog={() => setShowMaintenance(true)} />,
        }]
      : []),
    ...(canSeeFirearmTab
      ? [{ value: 'firearm', label: 'Firearm record', content: <FirearmTab asset={asset} /> }]
      : []),
  ];

  return (
    <section className="page">
      <Link className="as-back" to="/assets">← Inventory</Link>

      <header className="page-header">
        <div>
          <div className="as-detail-eyebrow">{TYPE_LABEL[asset.asset_type]}</div>
          <h1>{asset.name}</h1>
          <div className="as-detail-badges">
            <Badge tone={STATUS_TONE[asset.status]}>{STATUS_LABEL[asset.status]}</Badge>
            {isFirearm && <Badge tone="danger">controlled item</Badge>}
          </div>
        </div>
        <div className="page-toolbar">
          {can('asset.assign') && asset.status === 'available' && (
            <Button variant="primary" onClick={() => setCustodyAction('checkout')}>
              Check out
            </Button>
          )}
          {can('asset.assign') && asset.status === 'checked_out' && (
            <>
              <Button variant="ghost" onClick={() => setCustodyAction('transfer')}>
                Transfer
              </Button>
              <Button variant="primary" onClick={() => setCustodyAction('checkin')}>
                Check in
              </Button>
            </>
          )}
          {can('asset.write') && (
            <Button variant="ghost" onClick={() => setShowEdit(true)}>
              Edit
            </Button>
          )}
          {can('asset.delete') && (
            <Button variant="danger" onClick={() => setShowDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </header>

      <Card>
        <Card.Body>
          <dl className="detail-grid">
            <div><dt>Serial number</dt><dd>{asset.serial_number ? <code className="as-code">{asset.serial_number}</code> : '—'}</dd></div>
            <div><dt>Make / model</dt><dd>{asset.make_model ?? '—'}</dd></div>
            <div><dt>Barcode / tag</dt><dd>{asset.barcode ? <code className="as-code">{asset.barcode}</code> : '—'}</dd></div>
            <div><dt>Assigned site</dt><dd>{asset.assigned_site_id ?? '—'}</dd></div>
            <div><dt>Added</dt><dd>{fmtDate(asset.created_at)}</dd></div>
            <div><dt>Updated</dt><dd>{fmtDate(asset.updated_at)}</dd></div>
          </dl>
        </Card.Body>
      </Card>

      {asset.notes && (
        <Card>
          <Card.Header>
            <Card.Title>Notes</Card.Title>
          </Card.Header>
          <Card.Body>
            <p className="as-notes-text">{asset.notes}</p>
          </Card.Body>
        </Card>
      )}

      <Tabs items={tabItems} defaultValue="custody" />

      {showEdit && <EditAssetModal asset={asset} onClose={() => setShowEdit(false)} />}
      {custodyAction && (
        <CustodyModal
          asset={asset}
          action={custodyAction}
          onClose={() => setCustodyAction(null)}
        />
      )}
      {showMaintenance && (
        <MaintenanceModal assetId={asset.id} onClose={() => setShowMaintenance(false)} />
      )}
      <ConfirmDialog
        open={showDelete}
        title="Delete asset"
        message={`Remove “${asset.name}” from inventory? This is a soft delete and can be reversed by an administrator.`}
        confirmLabel="Delete"
        danger
        busy={deleteAsset.isPending}
        onConfirm={onDelete}
        onCancel={() => setShowDelete(false)}
      />
    </section>
  );
}

// ===========================================================================
// Custody timeline tab
// ===========================================================================

function CustodyTab({ assetId }: { assetId: number }): JSX.Element {
  const { data, isPending, error } = useAssetAssignments(assetId);

  if (isPending) return <p className="as-status">Loading custody timeline…</p>;
  if (error) return <p className="as-status as-error">Failed to load custody timeline.</p>;

  const events = data?.assignments ?? [];
  if (events.length === 0) {
    return <p className="as-tab-empty">No custody events recorded yet.</p>;
  }

  return (
    <ol className="as-timeline">
      {events.map((ev: AssetAssignment) => (
        <li key={ev.id} className="as-timeline-item">
          <div className="as-timeline-dot" aria-hidden="true" />
          <div className="as-timeline-body">
            <div className="as-timeline-row">
              <span className="as-timeline-event">{EVENT_LABEL[ev.event_type]}</span>
              <span className="as-timeline-time">{fmtDateTime(ev.event_at)}</span>
            </div>
            <div className="as-timeline-meta">
              {ev.personnel_id !== null && <span>Personnel #{ev.personnel_id}</span>}
              {ev.assignment_id !== null && <span>Assignment #{ev.assignment_id}</span>}
              {ev.condition && <span>Condition: {ev.condition}</span>}
              {ev.odometer !== null && <span>Odometer: {ev.odometer.toLocaleString()}</span>}
              {ev.round_count !== null && <span>Rounds: {ev.round_count.toLocaleString()}</span>}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ===========================================================================
// Maintenance tab
// ===========================================================================

function MaintenanceTab({
  assetId,
  onLog,
}: {
  assetId: number;
  onLog: () => void;
}): JSX.Element {
  const { data, isPending, error } = useAssetMaintenance(assetId);

  return (
    <div className="as-tab-panel">
      {can('maintenance.write') && (
        <div className="as-tab-toolbar">
          <Button variant="ghost" onClick={onLog}>
            Log maintenance
          </Button>
        </div>
      )}
      {isPending ? (
        <p className="as-status">Loading maintenance history…</p>
      ) : error ? (
        <p className="as-status as-error">Failed to load maintenance history.</p>
      ) : (data?.maintenance_records ?? []).length === 0 ? (
        <p className="as-tab-empty">No maintenance records.</p>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Performed</Table.HeaderCell>
              <Table.HeaderCell>Vendor</Table.HeaderCell>
              <Table.HeaderCell>Cost</Table.HeaderCell>
              <Table.HeaderCell>Next due</Table.HeaderCell>
              <Table.HeaderCell>Notes</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {(data?.maintenance_records ?? []).map((m: MaintenanceRecord) => (
              <Table.Row key={m.id}>
                <Table.Cell>{MAINTENANCE_LABEL[m.type]}</Table.Cell>
                <Table.Cell>{fmtDate(m.performed_at)}</Table.Cell>
                <Table.Cell tone="muted">{m.vendor ?? '—'}</Table.Cell>
                <Table.Cell>{fmtMoney(m.cost)}</Table.Cell>
                <Table.Cell>{fmtDate(m.next_due_at)}</Table.Cell>
                <Table.Cell tone="muted">{m.notes ?? '—'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
}

// ===========================================================================
// Firearm record tab (gated by asset.firearm_read on the caller; the page only
// mounts this for firearm assets when can('asset.firearm_read')).
// ===========================================================================

function FirearmTab({ asset }: { asset: Asset }): JSX.Element {
  return (
    <div className="as-firearm-panel">
      <p className="as-firearm-note">
        Restricted record — visible only to personnel with firearm access.
      </p>
      <dl className="detail-grid as-firearm-facts">
        <div><dt>Serial number</dt><dd>{asset.serial_number ? <code className="as-code">{asset.serial_number}</code> : '—'}</dd></div>
        <div><dt>Make / model</dt><dd>{asset.make_model ?? '—'}</dd></div>
        <div><dt>Status</dt><dd>{STATUS_LABEL[asset.status]}</dd></div>
        <div><dt>Asset tag</dt><dd>{asset.barcode ? <code className="as-code">{asset.barcode}</code> : '—'}</dd></div>
      </dl>
      <p className="as-firearm-note">
        Round counts and condition checks are captured on each custody event —
        see the Custody timeline tab for the full chain of custody.
      </p>
    </div>
  );
}

// ===========================================================================
// Edit modal
// ===========================================================================

function EditAssetModal({ asset, onClose }: { asset: Asset; onClose: () => void }): JSX.Element {
  const updateAsset = useUpdateAsset();

  const [name, setName] = useState(asset.name);
  const [serial, setSerial] = useState(asset.serial_number ?? '');
  const [makeModel, setMakeModel] = useState(asset.make_model ?? '');
  const [barcode, setBarcode] = useState(asset.barcode ?? '');
  const [status, setStatus] = useState<AssetStatus>(asset.status);
  const [notes, setNotes] = useState(asset.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const busy = updateAsset.isPending;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length === 0) return;
    try {
      await updateAsset.mutateAsync({
        id: asset.id,
        name: name.trim(),
        serial_number: serial.trim() || null,
        make_model: makeModel.trim() || null,
        barcode: barcode.trim() || null,
        status,
        notes: notes.trim() || null,
      });
      toast.success('Asset updated.');
      onClose();
    } catch (err) {
      setError(fieldError(err, 'Could not save changes. Try again.'));
    }
  };

  return (
    <Dialog
      title="Edit asset"
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-asset-form"
            variant="primary"
            disabled={busy || name.trim().length === 0}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form id="edit-asset-form" className="as-form" onSubmit={onSubmit}>
        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            disabled={busy}
            autoFocus
            required
          />
        </FormField>
        <FormRow>
          <FormField label="Serial number">
            <Input
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              maxLength={120}
              disabled={busy}
            />
          </FormField>
          <FormField label="Status">
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as AssetStatus)}
              disabled={busy}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Make / model">
            <Input
              value={makeModel}
              onChange={(e) => setMakeModel(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </FormField>
          <FormField label="Barcode / asset tag">
            <Input
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </FormField>
        </FormRow>
        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10_000}
            disabled={busy}
            style={{ resize: 'vertical', minHeight: 70 }}
          />
        </FormField>
        {error && <p className="as-error-banner" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// ===========================================================================
// Custody modal (checkout / checkin / transfer)
// ===========================================================================

const CUSTODY_TITLE: Record<'checkout' | 'checkin' | 'transfer', string> = {
  checkout: 'Check out asset',
  checkin: 'Check in asset',
  transfer: 'Transfer asset',
};

function CustodyModal({
  asset,
  action,
  onClose,
}: {
  asset: Asset;
  action: 'checkout' | 'checkin' | 'transfer';
  onClose: () => void;
}): JSX.Element {
  const checkout = useCheckoutAsset();
  const checkin = useCheckinAsset();
  const transfer = useTransferAsset();

  const isVehicle = asset.asset_type === 'vehicle';
  const isFirearm = asset.asset_type === 'firearm';
  const needsHolder = action !== 'checkin';

  const [personnelId, setPersonnelId] = useState('');
  const [assignmentId, setAssignmentId] = useState('');
  const [condition, setCondition] = useState('');
  const [odometer, setOdometer] = useState('');
  const [roundCount, setRoundCount] = useState('');
  const [eventAt, setEventAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = action === 'checkout' ? checkout : action === 'checkin' ? checkin : transfer;
  const busy = mutation.isPending;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const num = (s: string): number | undefined => {
      const n = Number(s.trim());
      return s.trim() !== '' && Number.isFinite(n) ? n : undefined;
    };
    const common = {
      id: asset.id,
      condition: condition.trim() || undefined,
      odometer: isVehicle ? num(odometer) : undefined,
      round_count: isFirearm ? num(roundCount) : undefined,
      event_at: eventAt ?? undefined,
    };
    try {
      if (action === 'checkin') {
        await checkin.mutateAsync(common);
      } else {
        await mutation.mutateAsync({
          ...common,
          personnel_id: num(personnelId),
          assignment_id: num(assignmentId),
        });
      }
      toast.success(`Asset ${action === 'checkout' ? 'checked out' : action === 'checkin' ? 'checked in' : 'transferred'}.`);
      onClose();
    } catch (err) {
      setError(fieldError(err, 'Could not record the custody event.'));
    }
  };

  return (
    <Dialog
      title={CUSTODY_TITLE[action]}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="custody-form" variant="primary" disabled={busy}>
            {busy ? 'Recording…' : CUSTODY_TITLE[action]}
          </Button>
        </>
      }
    >
      <form id="custody-form" className="as-form" onSubmit={onSubmit}>
        {needsHolder && (
          <FormRow>
            <FormField label="Personnel ID">
              <Input
                type="number"
                value={personnelId}
                onChange={(e) => setPersonnelId(e.target.value)}
                min={1}
                disabled={busy}
                placeholder="Officer receiving"
              />
            </FormField>
            <FormField label="Assignment ID">
              <Input
                type="number"
                value={assignmentId}
                onChange={(e) => setAssignmentId(e.target.value)}
                min={1}
                disabled={busy}
                placeholder="Optional"
              />
            </FormField>
          </FormRow>
        )}
        <FormField label="Condition">
          <Input
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            maxLength={2000}
            disabled={busy}
            placeholder="e.g. Good, scuffed, fully charged"
          />
        </FormField>
        {(isVehicle || isFirearm) && (
          <FormRow>
            {isVehicle && (
              <FormField label="Odometer">
                <Input
                  type="number"
                  value={odometer}
                  onChange={(e) => setOdometer(e.target.value)}
                  min={0}
                  disabled={busy}
                />
              </FormField>
            )}
            {isFirearm && (
              <FormField label="Round count">
                <Input
                  type="number"
                  value={roundCount}
                  onChange={(e) => setRoundCount(e.target.value)}
                  min={0}
                  disabled={busy}
                />
              </FormField>
            )}
          </FormRow>
        )}
        <FormField label="Event time">
          <DateInput value={eventAt} onChange={setEventAt} withTime disabled={busy} />
        </FormField>
        {error && <p className="as-error-banner" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// ===========================================================================
// Maintenance modal
// ===========================================================================

function MaintenanceModal({
  assetId,
  onClose,
}: {
  assetId: number;
  onClose: () => void;
}): JSX.Element {
  const create = useCreateMaintenanceRecord();

  const [type, setType] = useState<MaintenanceType>('inspection');
  const [vendor, setVendor] = useState('');
  const [cost, setCost] = useState('');
  const [performedAt, setPerformedAt] = useState<number | null>(null);
  const [nextDueAt, setNextDueAt] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const busy = create.isPending;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        id: assetId,
        type,
        vendor: vendor.trim() || undefined,
        cost: cost.trim() || undefined,
        performed_at: performedAt ?? undefined,
        next_due_at: nextDueAt ?? undefined,
        notes: notes.trim() || undefined,
      });
      toast.success('Maintenance logged.');
      onClose();
    } catch (err) {
      setError(fieldError(err, 'Could not log maintenance. Try again.'));
    }
  };

  return (
    <Dialog
      title="Log maintenance"
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="maintenance-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save record'}
          </Button>
        </>
      }
    >
      <form id="maintenance-form" className="as-form" onSubmit={onSubmit}>
        <FormRow>
          <FormField label="Type" required>
            <select
              className="form-input"
              value={type}
              onChange={(e) => setType(e.target.value as MaintenanceType)}
              disabled={busy}
            >
              {MAINTENANCE_TYPES.map((t) => (
                <option key={t} value={t}>{MAINTENANCE_LABEL[t]}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Performed">
            <DateInput value={performedAt} onChange={setPerformedAt} disabled={busy} />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Vendor">
            <Input
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </FormField>
          <FormField label="Cost (USD)">
            <Input
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              inputMode="decimal"
              pattern="\d+(\.\d{1,2})?"
              placeholder="e.g. 149.00"
              disabled={busy}
            />
          </FormField>
        </FormRow>
        <FormField label="Next due">
          <DateInput value={nextDueAt} onChange={setNextDueAt} disabled={busy} />
        </FormField>
        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10_000}
            disabled={busy}
            style={{ resize: 'vertical', minHeight: 70 }}
          />
        </FormField>
        {error && <p className="as-error-banner" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}
